import type { ReactNode } from 'react';
import { ImageCarousel } from '../files';
import { formatScanDate as defaultFormatScanDate } from './formatScanDate';
import { ScanHeader } from './ScanHeader';
import type { DmScansQueryConfig } from './useDmScansQueries';
import { useLatestScanDetail } from './useLatestScanDetail';
import { useScanPhotos } from './useScanPhotos';

export interface LatestScanPreviewProps {
    queries: DmScansQueryConfig;
    patientId: string | null;
    dmId: number | null | undefined;
    formatScanDate?: (value: string) => string;
    /** Hide the "Latest DM scan" eyebrow where the host card already labels it. */
    hideLabel?: boolean;
    /** Optional column beside the photo (indices, observations…). */
    sidePanel?: ReactNode;
    className?: string;
}

interface LatestScanBodyProps {
    dmId: number | null | undefined;
    isLoading: boolean;
    hasScan: boolean;
    images: string[];
    thumbnails: string[];
    labels: string[];
    resetKey: number | undefined;
    sidePanel?: ReactNode;
}

function LatestScanBody({
    dmId,
    isLoading,
    hasScan,
    images,
    thumbnails,
    labels,
    resetKey,
    sidePanel
}: LatestScanBodyProps) {
    if (!dmId) {
        return (
            <div className="flex-1 min-h-0 flex items-center justify-center">
                <span className="text-sm text-gray-500 text-center">
                    Not linked to Dental Monitoring
                </span>
            </div>
        );
    }
    if (isLoading) {
        return <div className="flex-1 min-h-0 bg-gray-200 rounded-lg animate-pulse" />;
    }
    if (!hasScan) {
        return (
            <div className="flex-1 min-h-0 flex items-center justify-center">
                <span className="text-sm text-gray-500">No scans yet</span>
            </div>
        );
    }
    const carousel = (
        <ImageCarousel
            images={images}
            labels={labels}
            thumbnails={thumbnails}
            resetKey={resetKey}
        />
    );
    if (!sidePanel) {
        return <div className="flex-1 min-h-0 rounded-lg">{carousel}</div>;
    }
    return (
        <div className="flex-1 min-h-0 grid grid-cols-[2fr_1fr] gap-4">
            <div className="min-h-0 rounded-lg">{carousel}</div>
            <div className="min-h-0 flex flex-col gap-3">{sidePanel}</div>
        </div>
    );
}

function LatestScanPreview({
    queries,
    patientId,
    dmId,
    formatScanDate = defaultFormatScanDate,
    hideLabel = false,
    sidePanel,
    className = 'flex-1 min-h-0 flex flex-col gap-3 border-t border-gray-100 pt-5'
}: LatestScanPreviewProps) {
    const {
        latestScan,
        detail,
        isLoading: isScanLoading
    } = useLatestScanDetail(queries, patientId, dmId);
    const { images, labels, thumbnails } = useScanPhotos(detail?.photos);

    return (
        <div className={className}>
            {/* Eyebrow and scan identity are stacked: one line overflows the host
                cards, which narrow a long way. */}
            <div className="flex flex-col gap-1.5 shrink-0">
                {!hideLabel && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        Latest DM scan
                    </span>
                )}
                <ScanHeader scan={latestScan} formatScanDate={formatScanDate} />
            </div>

            <LatestScanBody
                dmId={dmId}
                isLoading={isScanLoading}
                hasScan={!!latestScan}
                images={images}
                thumbnails={thumbnails}
                labels={labels}
                resetKey={latestScan?.scanId}
                sidePanel={sidePanel}
            />
        </div>
    );
}

export { LatestScanPreview };
